/**
 * Centralized style constants for Policy Tables
 * Simplified and DRY design for maintainability
 */

// ============================================================================
// COLORS - Single source of truth
// ============================================================================
export const COLORS = {
  // Status
  success: '#107c10',
  warning: '#c74f08', // Accessible warning orange - 4.62:1 contrast ratio (WCAG AA compliant)
  error: '#d13438',
  errorOnGray: '#e50000', // Accessible red for text on gray backgrounds - 4.60:1 contrast ratio (WCAG AA compliant)
  
  // Tags/Badges
  deployedBg: '#e6f4ea',
  deployedText: '#1e8e3e',
  notDeployedBg: '#fff4ce',
  notDeployedText: '#8a6914',
  
  // Grays (unified scale)
  gray50: '#fafafa',
  gray100: '#f8f9fa',
  gray200: '#f5f5f5',
  gray300: '#f0f0f0',
  gray400: '#e5e5e5',
  gray500: '#e0e0e0',
  gray600: '#6b7280',
  gray700: '#666',
  
  // Section variants (semantic aliases)
  createBg: '#e8f4fd',
  createContent: '#f0f7ff',
  createBorder: '#b3d9f7',
} as const;

// Section color schemes (derived from COLORS)
export const SECTION_COLORS = {
  default: { bg: COLORS.gray50, content: 'white', border: COLORS.gray400 },
  neutral: { bg: COLORS.gray200, content: COLORS.gray50, border: COLORS.gray500 },
  create: { bg: COLORS.createBg, content: COLORS.createContent, border: COLORS.createBorder },
} as const;

export const BORDER_RADIUS = { sm: 4, md: 6, lg: 8 } as const;

const border = (color: string) => `1px solid ${color}`;

export const TYPOGRAPHY = {
  heading: { fontWeight: 600, fontSize: 14 },
  body: { fontSize: 14 },
  caption: { fontSize: 12, color: COLORS.gray600 },
  small: { fontSize: 11 },
} as const;


export const RULE_ROW_STYLES = {
  container: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: border(COLORS.gray300),
    background: 'white',
    gap: 8,
  },
  expandedContent: {
    padding: '12px 16px 12px 48px',
    background: COLORS.gray100,
    borderBottom: border(COLORS.gray300),
  },
  ruleInfo: { flex: 1 },
  ruleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap' as const,
  },
  ruleName: { fontWeight: 600, fontSize: 14 },
  ruleDescription: {
    fontSize: 12,
    color: COLORS.gray600,
    marginTop: 4,
    display: 'block',
  },
  expandButton: { minWidth: 24, padding: 0 },
} as const;

const tagBase = { fontSize: 11, padding: '2px 8px', borderRadius: BORDER_RADIUS.sm, fontWeight: 600 };
export const TAG_STYLES = {
  deployed: { ...tagBase, background: COLORS.deployedBg, color: COLORS.deployedText },
  notDeployed: { ...tagBase, background: COLORS.notDeployedBg, color: COLORS.notDeployedText },
} as const;

const dialogSurface = (maxWidth: string) => ({ maxWidth, maxHeight: '85vh' });
export const DIALOG_STYLES = {
  surface: dialogSurface('850px'),
  surfaceLarge: dialogSurface('900px'),
  surfaceXLarge: dialogSurface('1200px'),
  content: { overflowY: 'auto' as const, maxHeight: '60vh' },
  contentFullHeight: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    height: '60vh',
  },
} as const;

export const GRID_STYLES = {
  twoColumn: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 },
  list: { margin: 0, paddingLeft: 20, fontSize: 13 },
  listItem: { marginBottom: 4 },
} as const;

export const SECTION_HEADER_STYLES = {
  default: { fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 8 },
  sticky: {
    padding: '12px 16px',
    background: COLORS.gray50,
    borderBottom: border(COLORS.gray400),
    fontWeight: 600,
    fontSize: 14,
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
  },
} as const;

const panelBase = { flex: 1, overflowY: 'auto' as const };
export const CONTAINER_STYLES = {
  bordered: { border: border(COLORS.gray400), borderRadius: BORDER_RADIUS.lg, overflow: 'hidden' as const },
  panel: panelBase,
  panelWithBorder: { ...panelBase, borderRight: border(COLORS.gray400) },
  infoMessage: { padding: '16px 24px', borderBottom: border(COLORS.gray400), background: COLORS.gray100 },
  emptyState: { padding: '24px 16px', textAlign: 'center' as const, color: COLORS.gray600 },
} as const;

export const getCoverageColor = (percent: number): string => {
  if (percent === 100) return COLORS.success;
  if (percent >= 60) return COLORS.warning;
  return COLORS.error;
};

export const getStatusBadgeColor = (status: 'deployed' | 'partial' | 'notDeployed') => {
  const map = {
    deployed: { background: COLORS.deployedBg, color: COLORS.deployedText },
    partial: { background: COLORS.notDeployedBg, color: COLORS.notDeployedText },
    notDeployed: { background: COLORS.gray200, color: COLORS.gray600 },
  };
  return map[status] ?? map.notDeployed;
};

// ============================================================================
// STATUS FILTER OPTIONS - Shared across Policy and Label Tables
// ============================================================================
// STATUS_OPTIONS moved to src/services/types.ts (shared across all entity types)
export { STATUS_OPTIONS } from '../../services/types';

// ============================================================================
// PRIORITY FILTER OPTIONS - Shared across Policy Tables
// ============================================================================
export const PRIORITY_OPTIONS = [
  { key: 'All', label: 'All Priorities' },
  { key: '0', label: 'Priority 0' },
  { key: '1', label: 'Priority 1' },
  { key: '2', label: 'Priority 2' },
  { key: '3', label: 'Priority 3' },
] as const;

// ============================================================================
// COMPONENT-SPECIFIC STYLES
// ============================================================================

/** CoverageCell layout styles */
export const COVERAGE_CELL_STYLES = {
  container: { display: 'flex', alignItems: 'center', gap: 8 } as const,
  countLabel: { fontWeight: 700 } as const,
  barWrapper: (maxWidth: number) => ({ flex: 1, maxWidth } as const),
  progressBar: { height: 3 } as const,
} as const;

/** CoverageCell tooltip text */
export const COVERAGE_TOOLTIP = {
  title: 'Partial Deployment Detected',
  description: (count: number, noun: string) =>
    `${count} tenant${count !== 1 ? 's' : ''} ${count !== 1 ? 'are' : 'is'} partially deployed with this ${noun}.`,
  hint: 'Click to expand and see affected tenants.',
} as const;

/** CollapsibleSection styles */
export const COLLAPSIBLE_STYLES = {
  wrapper: (borderColor: string) => ({
    border: `1px solid ${borderColor}`,
    borderRadius: BORDER_RADIUS.md,
    marginBottom: 16,
    overflow: 'hidden' as const,
  }),
  header: (bg: string) => ({
    padding: '12px 16px',
    background: bg,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    userSelect: 'none' as const,
  }),
  headerTitle: { fontWeight: 600, fontSize: 14 } as const,
  body: (bg: string) => ({ padding: '14px 16px', background: bg }),
  chevron: { fontSize: 12, color: COLORS.gray700 } as const,
} as const;

/** SitLabelSummary variant colors */
export const SIT_LABEL_VARIANT_COLORS = {
  existing: { icon: '#666', text: '#333', list: '#555' },
  new: { icon: '#0078d4', text: '#0078d4', list: undefined },
} as const;

/** RuleRow expanded content padding per variant */
export const RULE_ROW_PADDING = {
  default: '56px',
  readonly: '48px',
} as const;
